import { useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FormProvider, useForm } from "react-hook-form";
import { ReactComponent as SquareIcon } from "../icons/square.svg";
import { ReactComponent as CheckSquareIcon } from "../icons/check-square.svg";
import { ReactComponent as ErrorIcon } from "../icons/exclamation-circle.svg";
import { usePractice } from "../usePractice";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import { filename } from "../services/filename";
import { getBrand } from "../utils/brand";
import TextField from "../ui/TextField";
import SingleSelectField from "../ui/SingleSelectField";
import PhoneField from "../ui/PhoneField";
import CommunicationPrefField from "../ui/CommunicationPrefField";
import TextAreaField from "../ui/TextAreaField";
import AquamationTypeExplainer from "../ui/AquamationTypeExplainer";
import CheckboxField from "../ui/CheckboxField";
import SignatureBox from "../ui/SignatureBox";
import SpeciesField from "../ui/SpeciesField";
import NumberSelectField from "../ui/NumberSelectField";
import AddressField from "../ui/AddressField";

function CustomerIntakeForm() {
  const { practice, isLoadingPractice } = usePractice();
  const brand = getBrand(practice?.fields?.brand);
  const [formPage, setFormPage] = useState(1);
  const [caseData, setCaseData] = useState();
  const sigCanvas = useRef({});
  const processNewCase = httpsCallable(functions, "processNewCase");
  const [signatureIsEmpty, setSignatureIsEmpty] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmittedData, setLastSubmittedData] = useState(null);
  const loading = isLoadingPractice || isSubmitting;
  const [searchParams] = useSearchParams();

  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      ownerName: "",
      ownerEmail: "",
      ownerAddress: "",
      ownerTwoName: "",
      ownerTwoEmail: "",
      smsConsent: false,
      emailPreferred: true,
      smsPreferred: false,
      phonePreferred: false,
      petName: "",
      preferredPetName: "",
      weight: "",
      age: "",
      otherSpecies: "",
      breed: "",
      uniqueIdentifiers: "",
      excludeRemembranceItems: false,
      personalItemsToReturn: "",
    },
  });
  const { handleSubmit, watch, setValue } = methods;
  const aquamationType = watch("aquamationType");
  const excludeRemembranceItems = watch("excludeRemembranceItems");
  const weight = watch("weight");
  const species = watch("species");
  const personalItemInstructions = watch("personalItemInstructions");

  const fieldPages = {
    1: [
      "ownerName",
      "ownerEmail",
      "ownerPhone",
      "SMSConsent",
      "ownerAddress",
      "emailPreferred",
      "phonePreferred",
      "smsPreferred",
      "ownerTwoName",
      "ownerTwoEmail",
    ],
    2: [
      "petName",
      "preferredPetName",
      "weight",
      "sex",
      "species",
      "otherSpecies",
      "breed",
      "uniqueIdentifiers",
      "age",
    ],
    3: ["aquamationType"],
  };

  useEffect(() => {
    if (species !== "Other") {
      setValue("otherSpecies", "");
    }
  }, [species, setValue]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!caseData || caseData.error) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [caseData]);

  function fillDevData() {
    methods.reset({
      petName: "Fluffy",
      species: "Cat",
      weight: 9,
      age: 7,
      sex: "Female",
      breed: "Tabby",
      uniqueIdentifiers: "Brown spot on belly",
      ownerName: "Linda Harris",
      ownerAddress: "123 Main St, Anytown, USA",
      ownerEmail: "linda@example.com",
      ownerPhone: "+12065551234",
      ownerTwoName: "Andy Harris",
      ownerTwoEmail: "andy@example.com",
      ownerTwoPhone: "+12065551235",
      emailPreferred: true,
      smsPreferred: false,
      phonePreferred: false,
      aquamationType: "Remains Returned",
      preferredPetName: "",
      excludeRemembranceItems: false,
      specialInstructions:
        "Please play her favorite song during the aquamation: 'Eye of the Tiger by Survivor'",
      personalItemInstructions: "None",
    });
  }

  async function onSubmit(data) {
    setIsSubmitting(true);

    data.ownerName = data.ownerName.trim();
    if (data.ownerTwoName) {
      data.ownerTwoName = data.ownerTwoName.trim();
    }
    data.practice = new Array(practice.id);
    data.weight = parseInt(data.weight);
    data.age = String(data.age);
    data.formSubmissionTime = new Date();
    data.practiceName = practice.fields.practiceName;
    data.signatureUrl = sigCanvas.current.toDataURL("image/png");
    data.signedBy = data.ownerName;
    data.submitterType = "Customer";
    data.tagNumber = 0;
    data.filenameString = filename(data.petName);
    data.brand = brand;
    data.aquatory = practice.fields.defaultAquatory;
    data.aquatoryPrinterEmail = practice.fields.aquatoryPrinterEmail;

    setLastSubmittedData(data);

    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 30000)
      );

      const res = await Promise.race([processNewCase(data), timeoutPromise]);

      if (res.data.error) {
        res.data.fields = data;
        setCaseData(res.data);
      } else {
        setCaseData(res.data);
      }

      setCaseData((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          clayImpressions: data.clayImpressions,
          inkPrints: data.inkPrints,
          furClippings: data.furClippings,
        },
      }));
    } catch (error) {
      setCaseData({
        error: true,
        fields: data,
      });
    }

    setIsSubmitting(false);
  }

  if (loading)
    return <div className="loading-screen regular-2xl">Loading...</div>;

  if (!practice?.fields) {
    return <p>Could not load this practice. Please refresh and try again.</p>;
  }

  if (!practice.fields.enableCustomerForm) return <p>page not found</p>;

  if (caseData)
    return (
      <div className="container">
        {caseData.error ? (
          <>
            <p style={{ fontSize: "1.2rem" }}>
              <ErrorIcon
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  fill: "#dc2626",
                  verticalAlign: "middle",
                  marginRight: "0.5rem",
                }}
              />
              Sorry, there was an error submitting the form. Please notify the
              Family Services Coordinator at the front desk.
            </p>
            <button
              className="button mt-2"
              onClick={() => {
                setCaseData(null);
                if (lastSubmittedData) {
                  setIsSubmitting(true);
                  const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error("timeout")), 30000)
                  );
                  Promise.race([
                    processNewCase(lastSubmittedData),
                    timeoutPromise,
                  ])
                    .then((res) => {
                      if (res.data.error) {
                        res.data.fields = lastSubmittedData;
                        setCaseData(res.data);
                      } else {
                        setCaseData(res.data);
                      }

                      setCaseData((prev) => ({
                        ...prev,
                        fields: {
                          ...prev.fields,
                          clayImpressions: lastSubmittedData.clayImpressions,
                          inkPrints: lastSubmittedData.inkPrints,
                          furClippings: lastSubmittedData.furClippings,
                        },
                      }));
                    })
                    .catch(() => {
                      setCaseData({
                        error: true,
                        fields: lastSubmittedData,
                      });
                    })
                    .finally(() => setIsSubmitting(false));
                }
              }}
            >
              Retry
            </button>
          </>
        ) : (
          <p style={{ fontSize: "1.2rem" }} data-cy="thank-you">
            Thank you for providing the information for{" "}
            {caseData.fields.preferredPetName || caseData.fields.petName}. You
            will receive an email confirmation that will include further
            details.{" "}
            {searchParams.get("inPerson") && (
              <strong>
                Please return the iPad to the Family Services Coordinator at the
                front desk.
              </strong>
            )}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <div>
            <h4 className="mt-2">Pet Details</h4>
            <table>
              <tbody>
                <tr>
                  <td>
                    <strong>Name</strong>
                  </td>
                  <td>{caseData.fields.petName}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Preferred Name</strong>
                  </td>
                  <td>{caseData.fields.preferredPetName}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Weight</strong>
                  </td>
                  <td>{caseData.fields.weight}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Sex</strong>
                  </td>
                  <td>{caseData.fields.sex}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Species</strong>
                  </td>
                  <td>
                    {caseData.fields.species}
                    {caseData.fields.otherSpecies
                      ? ` - ${caseData.fields.otherSpecies}`
                      : ""}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Breed</strong>
                  </td>
                  <td>{caseData.fields.breed}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Unique Identifiers</strong>
                  </td>
                  <td>{caseData.fields.uniqueIdentifiers}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <h4 className="mt-2">Owner Details</h4>
            <table>
              <tbody>
                <tr>
                  <td>
                    <strong>Name</strong>
                  </td>
                  <td>{caseData.fields.ownerName}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Phone</strong>
                  </td>
                  <td>{caseData.fields.ownerPhone}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Email</strong>
                  </td>
                  <td>{caseData.fields.ownerEmail}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Address</strong>
                  </td>
                  <td>{caseData.fields.ownerAddress}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Second Owner</strong>
                  </td>
                  <td>{caseData.fields.ownerTwoName}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Second Email</strong>
                  </td>
                  <td>{caseData.fields.ownerTwoEmail}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Second Phone</strong>
                  </td>
                  <td>{caseData.fields.ownerTwoPhone}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Email Preferred</strong>
                  </td>
                  <td>
                    {caseData.fields.emailPreferred ? (
                      <CheckSquareIcon className="fill-green-500" />
                    ) : (
                      <SquareIcon className="fill-green-500" />
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>SMS Preferred</strong>
                  </td>
                  <td>
                    {caseData.fields.smsPreferred ? (
                      <CheckSquareIcon className="fill-green-500" />
                    ) : (
                      <SquareIcon className="fill-green-500" />
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Phone Preferred</strong>
                  </td>
                  <td>
                    {caseData.fields.phonePreferred ? (
                      <CheckSquareIcon className="fill-green-500" />
                    ) : (
                      <SquareIcon className="fill-green-500" />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <h4 className="mt-2">Aquamation Details</h4>
            <table>
              <tbody>
                <tr>
                  <td>
                    <strong>Aquamation Type</strong>
                  </td>
                  <td>{caseData.fields.aquamationType}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Exclude Remembrance Items</strong>
                  </td>
                  <td>
                    {caseData.fields.excludeRemembranceItems ? "Yes" : "No"}
                  </td>
                </tr>
                {!caseData.fields.excludeRemembranceItems &&
                  caseData.fields.aquamationType !== "Scattering Service" && (
                    <tr>
                      <td>
                        <strong>Remembrance Items</strong>
                      </td>
                      <td>
                        {`Clay Impressions: ${caseData.fields.clayImpressions}, Ink Prints:
                    ${caseData.fields.inkPrints}, Fur Clippings: ${caseData.fields.furClippings}`}
                      </td>
                    </tr>
                  )}
                <tr>
                  <td>
                    <strong>Special Instructions</strong>
                  </td>
                  <td>
                    {caseData.fields.specialInstructions
                      ? caseData.fields.specialInstructions
                      : "None"}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Personal Items</strong>
                  </td>
                  <td>
                    {caseData.fields.personalItemInstructions === "Return"
                      ? `Return: ${caseData.fields.personalItemsToReturn}`
                      : caseData.fields.personalItemInstructions}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );

  return (
    <div className="container">
      <Helmet>
        <title>{brand.name} - Intake Form</title>
      </Helmet>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} data-cy="customer-intake-form">
          {formPage === 1 && (
            <>
              <h3 className="mt-2 text-center">Owner Info</h3>

              <div className="fieldgroup">
                <TextField
                  name="ownerName"
                  label="Full Name"
                  required
                  validation={(value) =>
                    value.trim() === "" ||
                    value.trim().split(" ").length > 1 ||
                    "Please enter owner's full name"
                  }
                />
                <TextField
                  name="ownerEmail"
                  label="Email"
                  type="email"
                  required
                />
                <PhoneField
                  name="ownerPhone"
                  label="Phone"
                  required
                  showSMSConsent
                />
                <AddressField name="ownerAddress" label="Address" required />
                <CommunicationPrefField />
              </div>

              <h3 className="mt-2 text-center">2nd Owner Info</h3>

              <div className="fieldgroup">
                <TextField
                  name="ownerTwoName"
                  label="Full Name"
                  validation={(value) =>
                    value.trim() === "" ||
                    value.trim().split(" ").length > 1 ||
                    "Please enter second owner's full name"
                  }
                />
                <TextField name="ownerTwoEmail" label="Email" type="email" />
                <PhoneField name="ownerTwoPhone" label="Phone" />
              </div>
            </>
          )}

          {formPage === 2 && (
            <>
              <h3 className="text-center">Pet Info</h3>

              <div className="fieldgroup">
                <TextField name="petName" label="Pet Name" required />
                <TextField
                  name="preferredPetName"
                  label="Preferred Pet Name"
                  subtext="In case you would like any labels or certificates to use a different name"
                />
                <SpeciesField />
                <SingleSelectField
                  name="sex"
                  label="Sex"
                  options={[
                    { label: "Male", value: "Male" },
                    { label: "Female", value: "Female" },
                  ]}
                  required
                />
                {species === "Other" && (
                  <TextField
                    name="otherSpecies"
                    label="Species Detail"
                    required
                  />
                )}
                <TextField
                  name="weight"
                  label="Weight (lbs)"
                  type={"number"}
                  required
                />
                <TextField name="breed" label="Breed" required />
                <TextField
                  name="uniqueIdentifiers"
                  label="Unique Identifiers"
                  required
                  placeholder="Fur color, tripod, polydactyl, etc."
                />
                <TextField name="age" label="Approximate Age (yrs)" required />
              </div>
            </>
          )}

          {formPage === 3 && (
            <>
              <h3 className="mt-2 text-center">Aquamation Type</h3>
              <SingleSelectField
                name="aquamationType"
                options={[
                  {
                    label:
                      "Individual Aquamation Service with Remains Returned",
                    value: "Remains Returned",
                  },
                  {
                    label:
                      "Individual Aquamation Service with Scattering Service",
                    value: "Scattering Service",
                  },
                  { label: "Private Aquamation Service", value: "Private" },
                ]}
                required
                fullWidth
              />

              <AquamationTypeExplainer
                aquamationType={aquamationType}
                weight={weight}
              />
            </>
          )}

          {formPage === 4 && (
            <div className="remembrance-items-toggles">
              <h3 className="mt-2 text-center">Remembrance Items</h3>
              <p>
                <strong>
                  Your service comes with three complimentary remembrance items.
                </strong>
              </p>
              <NumberSelectField
                name="clayImpressions"
                label={"Clay paw impressions"}
                required
                disabled={excludeRemembranceItems}
              />
              <NumberSelectField
                name="inkPrints"
                label={"Ink paw prints"}
                required
                disabled={excludeRemembranceItems}
              />
              <NumberSelectField
                name="furClippings"
                label={"Fur clippings"}
                required
                disabled={excludeRemembranceItems}
              />
              <p>
                You can also purchase additional items:
                <ul>
                  <li>Ink prints or fur clippings: $16 each</li>
                  <li>Clay prints: $27 each</li>
                  <li>Package of all three: $32</li>
                </ul>
              </p>
              <CheckboxField
                name="excludeRemembranceItems"
                label={"Opt out of all remembrance items"}
              />
            </div>
          )}

          {formPage === 5 && (
            <>
              <h3 className="mt-2 text-center">Submit Form</h3>

              <div className="fieldgroup">
                <TextAreaField
                  name="specialInstructions"
                  label="Special Instructions"
                />
                <SingleSelectField
                  name="personalItemInstructions"
                  label="Are you leaving any personal items with your pet?"
                  options={[
                    {
                      label: "Yes, and I would like them returned",
                      value: "Return",
                    },
                    {
                      label: "Yes, and they can be donated",
                      value: "Donate",
                    },
                    {
                      label: "No",
                      value: "None",
                    },
                  ]}
                  required
                  fullWidth
                />
                {personalItemInstructions === "Return" && (
                  <TextAreaField
                    name="personalItemsToReturn"
                    label="Please describe the items you would like to be returned."
                    required
                  />
                )}
                <SignatureBox
                  canvasRef={sigCanvas}
                  setSignatureIsEmpty={setSignatureIsEmpty}
                />
              </div>
            </>
          )}

          <div className="mt-2 text-center">
            {formPage > 1 && (
              <button
                className="button button-black-700 spaced"
                type="button"
                onClick={() => {
                  if (
                    formPage === 5 &&
                    aquamationType === "Scattering Service"
                  ) {
                    setFormPage(3);
                  } else {
                    setFormPage(formPage - 1);
                  }
                }}
              >
                Back
              </button>
            )}
            {formPage < 5 && (
              <button
                className="button"
                type="button"
                onClick={async () => {
                  const isValid = await methods.trigger(fieldPages[formPage]);
                  if (isValid) {
                    if (
                      formPage === 3 &&
                      aquamationType === "Scattering Service"
                    ) {
                      setFormPage(5);
                    } else {
                      setFormPage(formPage + 1);
                    }
                  }
                }}
                data-cy="next"
              >
                Next
              </button>
            )}
            {formPage === 5 && (
              <button
                className="button"
                type="submit"
                disabled={signatureIsEmpty}
                data-cy="submit"
              >
                Submit
              </button>
            )}
          </div>
          {process.env.NODE_ENV === "development" && (
            <button
              onClick={(e) => {
                e.preventDefault();
                fillDevData();
              }}
            >
              Fill with dev data
            </button>
          )}
        </form>
      </FormProvider>
      <div>
        <a href="/privacy-policy" target="_blank">
          <small>Privacy Policy</small>
        </a>
      </div>
    </div>
  );
}

export default CustomerIntakeForm;
